import { useState } from "react";
import { useImageEditor } from "../../../components/ImageEditor";
import { BANNER_PRESETS, type BannerPreset } from "@atrium/shared";
import { cn } from "../../../lib/cn";
import { compact } from "../../../lib/format";
import { bannerBackground } from "../../../lib/palette";
import { CommunityIcon } from "../../../components/ui/Avatar";
import { Button } from "../../../components/ui/Button";
import { Field, Input, Textarea } from "../../../components/ui/Input";
import { UnsavedBar } from "../../../components/ui/Settings";
import { IconCamera, IconCheck, IconCrop, IconGlobe, IconImage, IconLock, IconTrash } from "../../../components/icons";
import { CommunityBanner } from "../CommunityBanner";
import { TopicInput } from "../CreateCommunityDialog";
import { useDraft, usePatchCommunity, type SectionProps } from "./shared";

type Img = { id: string; url: string } | null;

export function ProfileSection({ structure }: SectionProps) {
  const c = structure.community;
  const patch = usePatchCommunity(c.id);
  const d = useDraft({
    name: c.name,
    description: c.description,
    topics: c.topics,
    bannerColor: c.bannerColor,
  });
  // Images upload immediately but only apply on save.
  const [icon, setIcon] = useState<Img | undefined>(undefined);
  const [banner, setBanner] = useState<Img | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const iconEd = useImageEditor("community_icon", setIcon);
  const bannerEd = useImageEditor("community_banner", setBanner);

  const iconUrl = icon === undefined ? c.iconUrl : (icon?.url ?? null);
  const bannerUrl = banner === undefined ? c.bannerUrl : (banner?.url ?? null);
  const dirty = d.dirty || icon !== undefined || banner !== undefined;

  const save = async () => {
    setSaving(true);
    const ok = await patch({
      ...d.changed,
      ...(icon !== undefined ? { iconMediaId: icon?.id ?? null } : {}),
      ...(banner !== undefined ? { bannerMediaId: banner?.id ?? null } : {}),
    });
    setSaving(false);
    if (ok) {
      setIcon(undefined);
      setBanner(undefined);
    }
  };
  const reset = () => {
    d.reset();
    setIcon(undefined);
    setBanner(undefined);
  };

  return (
    <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_300px]">
      <div className="min-w-0 space-y-8">
        <Field label="Name" counter={`${d.draft.name.length}/50`}>
          {(p) => <Input {...p} value={d.draft.name} maxLength={50} onChange={(e) => d.set("name", e.target.value)} />}
        </Field>

        <section>
          <div className="mb-1 text-sm font-medium text-fg-2">Icon</div>
          <p className="mb-3 text-xs text-fg-3">Square images of at least 512×512 look best.</p>
          <div className="flex items-center gap-3">
            <CommunityIcon src={iconUrl} name={d.draft.name || "?"} size="lg" />
            <Button icon={IconCamera} onClick={iconEd.pick}>
              {iconUrl ? "Change icon" : "Upload icon"}
            </Button>
            {iconUrl && (
              <>
                <Button variant="ghost" icon={IconCrop} onClick={() => iconEd.adjust(iconUrl)}>
                  Adjust
                </Button>
                <Button variant="ghost" onClick={() => setIcon(null)}>
                  Remove
                </Button>
              </>
            )}
            {iconEd.dialog}
          </div>
        </section>

        <section>
          <div className="mb-1 text-sm font-medium text-fg-2">Banner</div>
          <p className="mb-3 text-xs text-fg-3">Pick a colour, or upload an image and frame it. An image covers the colour.</p>
          <div role="radiogroup" aria-label="Banner colour" className="grid grid-cols-5 gap-2 sm:max-w-md">
            {BANNER_PRESETS.map((p) => {
              const on = (d.draft.bannerColor ?? "graphite") === p;
              return (
                <button
                  key={p}
                  role="radio"
                  aria-checked={on}
                  aria-label={p}
                  onClick={() => d.set("bannerColor", p as BannerPreset)}
                  className={cn("relative aspect-[16/9] rounded-md transition-shadow", on ? "ring-2 ring-fg ring-offset-2 ring-offset-bg" : "hover:opacity-90")}
                  style={{ background: bannerBackground(p as BannerPreset) }}
                >
                  {on && <IconCheck className="icon absolute right-1 top-1 size-4 text-white drop-shadow" aria-hidden />}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex gap-2">
            <Button icon={IconImage} onClick={bannerEd.pick}>
              {bannerUrl ? "Change image" : "Upload image"}
            </Button>
            {bannerUrl && (
              <>
                <Button variant="ghost" icon={IconCrop} onClick={() => bannerEd.adjust(bannerUrl)}>
                  Adjust
                </Button>
                <Button variant="ghost" icon={IconTrash} onClick={() => setBanner(null)}>
                  Remove image
                </Button>
              </>
            )}
            {bannerEd.dialog}
          </div>
        </section>

        <Field label="Description" counter={`${d.draft.description.length}/500`} hint="Shown on the public page, invites and in discovery.">
          {(p) => <Textarea {...p} rows={4} value={d.draft.description} maxLength={500} placeholder="What's this community about?" onChange={(e) => d.set("description", e.target.value)} />}
        </Field>

        <Field label="Topics" hint="Up to 5. Topics connect the community to posts and discovery.">
          {() => <TopicInput value={d.draft.topics} onChange={(v) => d.set("topics", v)} />}
        </Field>

        <UnsavedBar show={dirty} saving={saving} onReset={reset} onSave={() => void save()} />
      </div>

      <aside className="lg:sticky lg:top-0 lg:self-start">
        <div className="mb-2 text-xs font-medium text-fg-3">Preview</div>
        <div className="overflow-hidden rounded-xl border border-line bg-bg-subtle">
          <CommunityBanner url={bannerUrl} preset={d.draft.bannerColor} className="h-24" />
          <div className="px-4 pb-4">
            <CommunityIcon src={iconUrl} name={d.draft.name || "?"} size="lg" className="relative z-10 -mt-6 ring-4 ring-bg-subtle" />
            <div className="mt-2 flex items-center gap-1.5">
              <span className="truncate text-base font-semibold">{d.draft.name || "Untitled"}</span>
              {c.visibility === "private" ? <IconLock className="icon size-4 text-fg-3" aria-label="Private" /> : <IconGlobe className="icon size-4 text-fg-3" aria-label="Public" />}
            </div>
            <div className="mt-0.5 flex items-center gap-3 text-xs text-fg-3 tabular">
              <span>{compact(c.memberCount)} {c.memberCount === 1 ? "member" : "members"}</span>
              <span>Est. {new Date(c.createdAt).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}</span>
            </div>
            {d.draft.description && <p className="mt-2 line-clamp-3 text-sm text-fg-2">{d.draft.description}</p>}
            {d.draft.topics.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {d.draft.topics.map((t) => (
                  <span key={t} className="rounded bg-surface-2 px-1.5 py-0.5 text-xs text-fg-2">
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
